import React, { useCallback, useEffect, useState } from "react";
import { ShieldCheck, Download, RefreshCw, Lock, Eye, Ban, AlertTriangle, Wand2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Shell } from "@/components/Shell";
import { AiTrustBadge } from "@/components/AiTrustBadge";
import { AiFeatureMap } from "@/components/AiFeatureMap";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const MODES = [
  {
    key: "standard",
    icon: Eye,
    label: "Standard",
    blurb: "Email addresses and any secret-shaped tokens are stripped before anything leaves. Suitable for internal and commercial work.",
  },
  {
    key: "strict",
    icon: Lock,
    label: "Strict (recommended for NHS & public sector)",
    blurb: "Adds monetary values, staff names (pseudonymised to [person-1] so reports still read correctly), phone numbers and 10-digit identifiers.",
  },
  {
    key: "off",
    icon: Ban,
    label: "Off",
    blurb: "AI features are hard-disabled for this workspace. Calls are refused at the boundary, so no payload can leave for any reason.",
  },
];

export default function AdminAI() {
  const [policy, setPolicy] = useState(null);
  const [audit, setAudit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sample, setSample] = useState("");
  const [people, setPeople] = useState("");
  const [preview, setPreview] = useState(null);
  const [filters, setFilters] = useState({ caller: "", outcome: "" });
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(() => {
    api.get("/ai/egress-policy").then(({ data }) => setPolicy(data))
      .catch((e) => toast.error(apiError(e.response?.data?.detail) || "Could not load the egress policy"));
    api.get("/ai/audit", { params: { limit: 100, ...(filters.caller ? { caller: filters.caller } : {}), ...(filters.outcome ? { outcome: filters.outcome } : {}) } })
      .then(({ data }) => setAudit(data)).catch(() => {});
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const runPreview = async () => {
    try {
      const { data } = await api.post("/ai/redaction-preview", {
        text: sample || null,
        people: people ? people.split(",").map((p) => p.trim()).filter(Boolean) : null,
      });
      setPreview(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Preview failed");
    }
  };

  const setRetention = async (days) => {
    try {
      await api.put("/ai/egress-policy", { ai_egress_mode: policy.ai_egress_mode, ai_audit_retention_days: Number(days) });
      await load();
      toast.success(`Audit retention set to ${days} days`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const setMode = async (mode) => {
    if (mode === "off" && !window.confirm(
      "Switch AI off for this workspace?\n\n" +
      "These five features move to manual entry — none of them break:\n" +
      "• Weekly Report drafting — you write it in the same form\n" +
      "• Status report draft & suggested RAGs — you set them yourself\n" +
      "• Risk & issue auto-classification — you pick from the dropdowns\n" +
      "• Risk mitigation suggestions — you type the mitigation\n" +
      "• Overdue chase-up summary — the list still generates, only the paragraph is omitted\n\n" +
      "Everything else — RAID, schedule, documents, approvals, compliance, reporting, BI feeds — " +
      "is unaffected. Calls are then refused at the boundary, so no payload can leave for any reason."
    )) return;
    setBusy(true);
    try {
      await api.put("/ai/egress-policy", { ai_egress_mode: mode });
      await load();
      toast.success(mode === "off" ? "AI features switched off for this workspace" : `Egress mode set to ${mode}`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/ai/audit/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "ai-egress-audit.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Export failed");
    }
  };

  return (
    <Shell
      testId="admin-ai-page"
      title="AI & Data Egress"
      subtitle="Control what the Insights Assistant may send outside your estate, and evidence every call it made."
      actions={
        <div className="flex items-center gap-2">
          <AiTrustBadge />
          <Button variant="outline" data-testid="audit-refresh-btn" onClick={load} className="gap-2">
            <RefreshCw size={14} /> Refresh
          </Button>
          <Button data-testid="audit-export-btn" onClick={exportCsv} className="gap-2" style={{ backgroundColor: "var(--brand-primary)" }}>
            <Download size={14} /> Export Audit (CSV)
          </Button>
        </div>
      }
    >
      {!policy ? (
        <p className="py-8 text-center text-sm text-slate-400">Loading policy…</p>
      ) : (
        <div className="space-y-6">
          <section className="grid grid-cols-1 gap-4 lg:grid-cols-3" data-testid="egress-modes">
            {MODES.map((m) => {
              const active = policy.ai_egress_mode === m.key;
              return (
                <button
                  key={m.key}
                  data-testid={`egress-mode-${m.key}`}
                  disabled={busy}
                  onClick={() => setMode(m.key)}
                  className={`panel p-5 text-left transition-shadow hover:shadow-md ${active ? "ring-2 ring-[color:var(--brand-primary)]" : ""}`}
                >
                  <div className="flex items-center gap-2">
                    <m.icon size={16} style={{ color: active ? "var(--brand-primary)" : "#94A3B8" }} />
                    <h3 className="font-display text-sm font-semibold text-slate-900">{m.label}</h3>
                  </div>
                  <p className="mt-2 text-xs text-slate-600">{m.blurb}</p>
                  {active && (
                    <span data-testid={`egress-active-${m.key}`} className="mt-3 inline-block rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700">
                      Active
                    </span>
                  )}
                </button>
              );
            })}
          </section>

          <section className="panel p-5" data-testid="egress-vendor-note">
            <h3 className="flex items-center gap-2 font-display text-base font-semibold text-slate-800">
              {policy.tenant_bound
                ? <ShieldCheck size={15} className="text-emerald-600" />
                : <AlertTriangle size={15} className="text-amber-500" />}
              {policy.tenant_bound ? "Data boundary" : "Outstanding assurance item"}
            </h3>
            <p className="mt-2 text-sm text-slate-600">{policy.vendor_note}</p>
            <p className="mt-2 text-xs text-slate-500">
              Everything else is enforced in this application: secrets can never be read into a payload,
              redaction happens before the call, and each call is logged below with its redacted prompt,
              redacted response and a digest of what was sent.
            </p>
          </section>

          <AiFeatureMap />

          <section className="panel p-5" data-testid="redaction-preview">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <h3 className="flex items-center gap-2 font-display text-base font-semibold text-slate-800">
                  <Wand2 size={15} className="text-slate-400" /> Redaction preview
                </h3>
                <p className="mt-1 text-xs text-slate-500">
                  Paste any text to see exactly what would leave under the active mode. Nothing is sent anywhere.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  data-testid="redaction-people-input"
                  className="h-9 w-48 rounded border border-slate-200 px-2 text-sm"
                  placeholder="Names to pseudonymise"
                  value={people}
                  onChange={(e) => setPeople(e.target.value)}
                />
                <Button variant="outline" data-testid="redaction-run-btn" onClick={runPreview} className="gap-2">
                  <Wand2 size={14} /> Preview
                </Button>
              </div>
            </div>
            <textarea
              data-testid="redaction-input"
              className="mt-3 h-24 w-full rounded border border-slate-200 p-3 font-mono text-xs"
              placeholder="Leave blank to use a worked NHS-style example containing a name, budget, email, phone and connection string."
              value={sample}
              onChange={(e) => setSample(e.target.value)}
            />
            {preview && (
              <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <span className="field-label">Before</span>
                  <p className="rounded bg-red-50 p-3 font-mono text-xs text-slate-700" data-testid="redaction-before">{preview.before}</p>
                </div>
                <div>
                  <span className="field-label">After — what actually leaves</span>
                  <p className="rounded bg-emerald-50 p-3 font-mono text-xs text-slate-700" data-testid="redaction-after">
                    {preview.would_send ? preview.after : "Nothing — AI is switched off for this workspace."}
                  </p>
                </div>
              </div>
            )}
          </section>

          <section className="panel overflow-x-auto" data-testid="egress-audit-table">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-5 py-3">
              <h3 className="flex items-center gap-2 font-display text-sm font-semibold text-slate-800">
                <ShieldCheck size={15} className="text-slate-400" /> Outbound AI Audit Trail
              </h3>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  data-testid="audit-filter-caller"
                  className="h-8 w-44 rounded border border-slate-200 px-2 text-xs"
                  placeholder="Filter by feature"
                  value={filters.caller}
                  onChange={(e) => setFilters({ ...filters, caller: e.target.value })}
                />
                <select
                  data-testid="audit-filter-outcome"
                  className="h-8 rounded border border-slate-200 px-2 text-xs"
                  value={filters.outcome}
                  onChange={(e) => setFilters({ ...filters, outcome: e.target.value })}
                >
                  <option value="">All outcomes</option>
                  <option value="sent">Sent</option>
                  <option value="failed">Failed</option>
                </select>
                <select
                  data-testid="audit-retention-select"
                  className="h-8 rounded border border-slate-200 px-2 text-xs"
                  value={audit?.retention_days ?? 90}
                  onChange={(e) => setRetention(e.target.value)}
                >
                  {[30, 90, 180, 365, 2555].map((d) => (
                    <option key={d} value={d}>{`Retain ${d} days`}</option>
                  ))}
                </select>
                <span className="text-xs text-slate-500" data-testid="audit-total">
                  {audit?.total ?? 0} calls
                </span>
              </div>
            </div>
            {!audit?.rows?.length ? (
              <p className="py-8 text-center text-sm text-slate-400" data-testid="audit-empty">
                No outbound AI calls recorded yet.
              </p>
            ) : (
              <table className="w-full min-w-[1000px] text-sm">
                <thead className="bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-500">
                  <tr>
                    {["When", "Feature", "Engine", "Boundary", "Mode", "Size", "Redactions", "Latency", "Result", ""].map((h) => (
                      <th key={h} className="px-3 py-2 font-semibold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {audit.rows.map((r) => (
                    <React.Fragment key={r.id}>
                      <tr data-testid={`audit-row-${r.id}`} className="transition-colors hover:bg-slate-50">
                        <td className="px-3 py-2 text-xs text-slate-500">{String(r.ts).slice(0, 19).replace("T", " ")}</td>
                        <td className="px-3 py-2 text-xs text-slate-700">{r.caller}</td>
                        <td className="px-3 py-2 text-xs text-slate-600">{r.model}</td>
                        <td className="px-3 py-2 text-xs">
                          <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${r.tenant_bound ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                            {r.tenant_bound ? "Tenant-bound" : "External"}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-600">{r.egress_mode}</td>
                        <td className="px-3 py-2 text-xs text-slate-600">{r.payload_bytes} B</td>
                        <td className="px-3 py-2 text-xs text-slate-600">
                          {Object.keys(r.redactions || {}).length
                            ? Object.entries(r.redactions).map(([k, v]) => `${k}×${v}`).join(", ")
                            : "—"}
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-600">{r.latency_ms} ms</td>
                        <td className="px-3 py-2">
                          <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${r.ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
                            {r.ok ? "Sent" : "Failed"}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <button
                            data-testid={`audit-expand-${r.id}`}
                            onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                            className="text-xs font-semibold text-[color:var(--brand-primary)] hover:underline"
                          >
                            {expanded === r.id ? "Hide" : "Evidence"}
                          </button>
                        </td>
                      </tr>
                      {expanded === r.id && (
                        <tr data-testid={`audit-detail-${r.id}`}>
                          <td colSpan={10} className="bg-slate-50 px-5 py-4">
                            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                              <div>
                                <span className="field-label">Redacted prompt sent</span>
                                <p className="max-h-48 overflow-y-auto whitespace-pre-wrap rounded bg-white p-3 font-mono text-[11px] text-slate-700">
                                  {r.prompt_sanitised || "Not recorded for this call."}
                                </p>
                              </div>
                              <div>
                                <span className="field-label">Redacted response received</span>
                                <p className="max-h-48 overflow-y-auto whitespace-pre-wrap rounded bg-white p-3 font-mono text-[11px] text-slate-700">
                                  {r.response_sanitised || r.error || "No response recorded."}
                                </p>
                              </div>
                            </div>
                            <p className="mt-2 font-mono text-[10px] text-slate-400">
                              SHA-256 of sent payload: {r.payload_sha256}
                            </p>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            )}
            {audit?.note && <p className="px-5 py-3 text-xs text-slate-500">{audit.note}</p>}
          </section>
        </div>
      )}
    </Shell>
  );
}
